import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import DataTable from '../../components/DataTable';
import { listPurchases } from '../../services/cloneProvider';
import { getErrorMessage, formatVnd, formatDateTime, formatNumber } from './badges';
import useUrlState, { useUrlStateBatch } from './useUrlState';

const WEBSITES = ['clone2fa', 'sellmup', 'cloneviet', 'muaview'];
const PAGE_SIZE = 50;
const SERVICE_TYPE_LABEL = { fb_ql: 'FB QL', fb_eaaaau: 'FB EAAAAU', fb_eaagno: 'FB EAAGNO' };
const SERVICE_TYPE_SHORT_LABEL = { fb_ql: 'QL', fb_eaaaau: 'EAAAAU', fb_eaagno: 'EAAGNO' };

export default function PurchasesTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [pageStr, setPageStr] = useUrlState('purchases', 'page', '1');
  const page = Number(pageStr);
  const setPage = useCallback(
    (next) => setPageStr((prev) => String(typeof next === 'function' ? next(Number(prev)) : next)),
    [setPageStr]
  );
  const [isLoading, setIsLoading] = useState(true);
  const [website] = useUrlState('purchases', 'website', '');
  const [purchasedFrom] = useUrlState('purchases', 'purchased_at_from', '');
  const [purchasedTo] = useUrlState('purchases', 'purchased_at_to', '');
  const [sortBy] = useUrlState('purchases', 'sort_by', 'purchased_at');
  const [sortAscStr] = useUrlState('purchases', 'sort_asc', 'false');
  const sortAsc = sortAscStr === 'true';
  const setUrlBatch = useUrlStateBatch('purchases');

  // Draft filter - chỉ áp dụng (fetch + lưu URL) khi bấm nút "Lọc", tránh
  // set liên tiếp nhiều useUrlState setter trong 1 handler (xem comment
  // useUrlStateBatch trong useUrlState.js).
  const [draftWebsite, setDraftWebsite] = useState(website);
  const [draftPurchasedFrom, setDraftPurchasedFrom] = useState(purchasedFrom);
  const [draftPurchasedTo, setDraftPurchasedTo] = useState(purchasedTo);

  const applyFilters = useCallback(() => {
    setUrlBatch(
      { page: 1, website: draftWebsite, purchased_at_from: draftPurchasedFrom, purchased_at_to: draftPurchasedTo },
      { page: 1, website: '', purchased_at_from: '', purchased_at_to: '' }
    );
  }, [setUrlBatch, draftWebsite, draftPurchasedFrom, draftPurchasedTo]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = { limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE, sort_by: sortBy, sort_asc: sortAsc };
      if (website) params.website = website;
      if (purchasedFrom) params.purchased_at_from = purchasedFrom;
      if (purchasedTo) params.purchased_at_to = purchasedTo;
      const data = await listPurchases(params);
      setItems(data.items);
      setTotal(data.paging.total_item);
      setSummary(data.summary);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  }, [page, website, purchasedFrom, purchasedTo, sortBy, sortAsc]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const columns = [
    { key: 'purchased_at', label: 'Purchased at', sortable: true, render: (row) => formatDateTime(row.purchased_at) },
    { key: 'website', label: 'Website' },
    { key: 'product_name', label: 'Product', maxWidth: 220, wrap: 'truncate' },
    { key: 'account_count', label: 'Accounts' },
    { key: 'unit_vnd', label: 'Unit', render: (row) => formatVnd(row.unit_vnd) },
    { key: 'total_vnd', label: 'Total', render: (row) => formatVnd(row.total_vnd) },
    {
      key: 'remain_working',
      label: 'Remaining',
      render: (row) => `${row.remain_working}/${row.account_count}`,
    },
    {
      key: 'total_request',
      label: 'Requests',
      render: (row) => {
        const entries = Object.entries(row.request_type_count || {}).filter(([, count]) => count > 0);
        if (entries.length === 0) return formatNumber(row.total_request);
        return (
          <div className="flex flex-col gap-0.5 text-xs">
            {entries.map(([serviceType, count]) => (
              <span key={serviceType}>
                {SERVICE_TYPE_SHORT_LABEL[serviceType] || serviceType}: {formatNumber(count)}
              </span>
            ))}
          </div>
        );
      },
    },
    { key: 'est_cost_vnd', label: 'Est. cost', render: (row) => formatNumber(row.est_cost_vnd) },
  ];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-cyan-100 bg-white p-3">
        <select
          value={draftWebsite}
          onChange={(e) => setDraftWebsite(e.target.value)}
          className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
        >
          <option value="">Website: Tất cả</option>
          {WEBSITES.map((w) => <option key={w} value={w}>{w}</option>)}
        </select>
        <label className="flex items-center gap-1.5 text-xs text-slate-500">
          Purchased từ
          <input
            type="datetime-local"
            value={draftPurchasedFrom}
            onChange={(e) => setDraftPurchasedFrom(e.target.value)}
            className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
          />
        </label>
        <label className="flex items-center gap-1.5 text-xs text-slate-500">
          đến
          <input
            type="datetime-local"
            value={draftPurchasedTo}
            onChange={(e) => setDraftPurchasedTo(e.target.value)}
            className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
          />
        </label>
        <button
          type="button"
          onClick={applyFilters}
          className="rounded-lg bg-cyan-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-cyan-700"
        >
          Lọc
        </button>
      </div>

      {summary && (
        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-xl border border-cyan-100 bg-white p-3">
            <p className="text-xs uppercase tracking-wide text-slate-500">Tổng tiền</p>
            <p className="mt-1 text-lg font-bold text-slate-900">{formatVnd(summary.total_money)}</p>
          </div>
          <div className="rounded-xl border border-cyan-100 bg-white p-3">
            <p className="text-xs uppercase tracking-wide text-slate-500">Tổng request</p>
            <p className="mt-1 text-lg font-bold text-slate-900">{formatNumber(summary.total_request)}</p>
          </div>
          <div className="rounded-xl border border-cyan-100 bg-white p-3">
            <p className="text-xs uppercase tracking-wide text-slate-500">Avg cost</p>
            <p className="mt-1 text-lg font-bold text-slate-900">
              {summary.avg_cost != null ? `${formatVnd(summary.avg_cost)}/request` : '—'}
            </p>
          </div>
        </div>
      )}

      {summary?.total_request_by_service_type && (
        <div className="grid grid-cols-3 gap-3">
          {Object.entries(summary.total_request_by_service_type).map(([serviceType, count]) => (
            <div key={serviceType} className="rounded-xl border border-cyan-100 bg-white p-3">
              <p className="text-xs uppercase tracking-wide text-slate-500">
                Request {SERVICE_TYPE_LABEL[serviceType] || serviceType}
              </p>
              <p className="mt-1 text-lg font-bold text-slate-900">{formatNumber(count)}</p>
            </div>
          ))}
        </div>
      )}

      <DataTable
        columns={columns}
        data={items}
        isLoading={isLoading}
        sortBy={sortBy}
        sortDir={sortAsc ? 'asc' : 'desc'}
        onSortChange={(key, dir) => setUrlBatch({ sort_by: key, sort_asc: dir === 'asc' }, { sort_by: 'purchased_at', sort_asc: false })}
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        onPageChange={setPage}
      />
    </div>
  );
}
